import React, {FC} from 'react';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {CardModel} from '../../models/api/cardModel';
import {DomainModel, DomainValidityStatusesEnum} from '../../models/api/domainModel';
import {AccountDetailsSectionsEnum} from '../../models/api/localPreferencesModel';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {ContactDetailsPanelAccountDetailsSubState} from '../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactDetailsPanelId} from '../../store/panels/panelStateBase';
import {AccountDetailsContainer} from '../accounts/accountDetails/accountDetailsContainer';
import {AccountDetailsHeaderSkeleton} from '../accounts/accountDetails/loadingSkeletons/accountDetailsHeaderSkeleton';
import {AccountDetailsSectionSkeleton} from '../accounts/accountDetails/loadingSkeletons/accountDetailsSectionSkeleton';
import {ContactDetailsNoAccountContainer} from './accounts/contactDetailsNoAccountContainer';
import {ContactDetailsNoAccountCustomDomainContainer} from './accounts/contactDetailsNoAccountCustomDomainContainer';
import {contactDetailsAccountDetailsTrackingHandler} from './helpers/contactDetailsAccountDetailsTracking';

interface ContactDetailsAccountDetailsWrapperProps {
  panelId: ContactDetailsPanelId;
  card: CardModel | undefined;
  contact: VisualContactModel | undefined;
  panelLoadingStatus?: AsyncStatusesEnum;
  subState: ContactDetailsPanelAccountDetailsSubState;
  selectedDomain?: DomainModel;
}

/*
 * Component.
 */

export const ContactDetailsAccountDetailsWrapper: FC<ContactDetailsAccountDetailsWrapperProps> = (props) => {
  const {panelId, card, contact, subState, selectedDomain, panelLoadingStatus} = props;
  const accountId = subState.accountId ?? card?.accountId;

  const shouldShowLoadingState = selectedDomain?.validityStatus === DomainValidityStatusesEnum.TO_BE_CHECKED;
  const shouldShowEmptyState =
    selectedDomain?.validityStatus === DomainValidityStatusesEnum.INVALID_CONFLICT && !accountId;

  // If we should show a loading state
  if (shouldShowLoadingState || panelLoadingStatus === AsyncStatusesEnum.LOADING) {
    return (
      <>
        <AccountDetailsHeaderSkeleton />
        <AccountDetailsSectionSkeleton section={AccountDetailsSectionsEnum.CONVERSATIONS} />
      </>
    );
  }

  // We have no accounts and at least 1 custom domain
  if (selectedDomain && !shouldShowEmptyState) {
    return (
      <ContactDetailsNoAccountCustomDomainContainer
        card={card}
        panelId={panelId}
        contact={contact}
        customDomain={selectedDomain.name}
        conversationStatus={subState.conversationStatus}
        conversationIds={subState.conversationIds}
        hasMoreConversations={subState.hasMoreConversations}
      />
    );
  }

  // If we do not have an account or should force showing an empty state
  if (!accountId || shouldShowEmptyState) {
    return <ContactDetailsNoAccountContainer card={card} panelId={panelId} contact={contact} />;
  }

  return (
    <AccountDetailsContainer
      panelId={panelId}
      accountId={accountId}
      status={subState.status}
      cardsState={subState.cardsState}
      conversationStatus={subState.conversationStatus}
      conversationIds={subState.conversationIds}
      hasMoreConversations={subState.hasMoreConversations}
      onTrackEvent={(event) => contactDetailsAccountDetailsTrackingHandler(card, event)}
      isEmbedded
    />
  );
};
